/* eslint-disable react-hooks/exhaustive-deps */
import React, { useRef, useState, useEffect, ChangeEvent } from 'react';
import {
  Box,
  Switch,
  Divider,
  Popover,
  TableRow,
  TableCell,
  IconButton,
  Typography,
  CircularProgress
} from '@mui/material';
import _ from 'lodash';
import moment from 'moment';
import { ClockIcon, AnswerIcon, DeleteIcon, PreviewIcon } from '../../../../../../utils/Icons';
import {
  roleNameFormatter,
  unixToHumanReadable,
  formatNumberWithCommas,
  GroupBasedRoleFormatter
} from '../../../../components/common';
import { toast } from 'react-toastify';
import { API } from '../../../../../../api';
import BlockIcon from '@mui/icons-material/Block';
import Upvotes from '../../../../components/Upvotes';
import ProgramDeletDialog from '../ProgramDeletDialog';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useNavigate, useParams } from 'react-router-dom';
import ViewObjectivesDialog from '../ViewObjectivesDialog';
import RoleTypeTag from '../../../../components/RoleTypeTag';
import { useModule } from '../../../../../../utils/useModule';
import { getUserDetails } from '../../../../../../utils/orgName';
import ChatAvatarComponent from '../../../chat/ChatAvatarComponent';
import { useAppDispatch, useAppSelector } from '../../../../../../redux/hooks';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import LinearProgressWithLabel from '../../../Matches/components/LinearProgressWithLabel';
import { acsTokenActions } from '../../../../../../redux/slices/acs-token/acsTokenSlice';
import { acsChannelActions } from '../../../../../../redux/slices/acs-channels/acsChannels';
import { IGroupUser, groupUsersActions } from '../../../../../../redux/slices/group-users/groupUsersSlice';
import { communityUsersActions } from '../../../../../../redux/slices/acs-community-members/acsCommunityMembers';

interface Props {
  data: IGroupUser;
  closedUserId: any;
  setShowProfile: any;
  setActiveUserId: any;
  scrollToUserCall: any;
  setScrollToUserCall: any;
  showGoalProgress: boolean;
  handleOpenTasks: any;
  showCompany?: boolean;
}

const ProgramUserTableRow = ({
  data,
  setActiveUserId,
  setShowProfile,
  scrollToUserCall,
  showGoalProgress,
  setScrollToUserCall,
  closedUserId,
  handleOpenTasks,
  showCompany
}: Props) => {
  const grpId = useParams().id || '';
  const { isMasterclass } = useModule();
  const { isMentorship } = useModule();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const usersLoginDetails = useAppSelector((state) => state.groupUsers.data?.users);

  const companyName = data.company?.companyName || '-';
  const companyLocation = data.company?.companyLocation || '-';

  const [openDelete, setDelete] = useState(false);
  const [openActive, setActiveDialog] = useState(false);
  const [deleteLoader, setDeleteLoader] = useState(false);
  const [anchorAction, setAnchorAction] = useState<HTMLButtonElement | null>(null);
  const [objectivesData, setObjectivesData] = useState<any[]>([]);
  const [defaultMatch, setDefaultMatch] = useState(false);
  const [switchLoading, setSwitchLoading] = useState(false);

  // eslint-disable-next-line no-extra-boolean-cast
  const id = Boolean(anchorAction) ? 'simple-popover' : undefined;

  const activeItemRef = useRef<any>(null);

  /** SCROLL TO USER */
  const scrollToUser = () => {
    if (activeItemRef.current) {
      activeItemRef?.current?.scrollIntoView({ block: 'center' });
    }
  };

  useEffect(() => {
    if (scrollToUserCall) {
      scrollToUser();
      setScrollToUserCall(false);
    }
  }, [scrollToUserCall === true]);

  useEffect(() => {
    setDefaultMatch(data.defaultMatch?.includes(grpId) ?? false);
  }, [data]);

  /** ACTION MENU */
  const handleClickAction = (event: any) => {
    setAnchorAction(event.currentTarget);
  };

  const handleCloseAction = () => setAnchorAction(null);

  /** REMOVE MEMBER */
  const removeUser = async () => {
    setDeleteLoader(true);
    try {
      const response = await API.removeUserFromGroup({
        grpId,
        userId: data.userId
      });

      if (response.status === 200) {
        toast.success('User removed successfully');
        dispatch(groupUsersActions.removeUser({ userId: data.userId }));
        setDelete(false);
        setAnchorAction(null);
      }
    } catch {
      toast.error('User failed to be removed');
    }
    setDeleteLoader(false);
  };

  /** ACTIVATE / DEACTIVATE */
  const activateUser = async () => {
    setDeleteLoader(true);
    try {
      const response = await API.activateUser({
        isActive: !data.isActive,
        grpId,
        userId: data.userId
      });

      if (response.status === 200) {
        dispatch(
          groupUsersActions.updateUser({
            ...data,
            isActive: !data.isActive
          })
        );
        toast.success(`User ${data.isActive ? 'deactivated' : 'activated'} successfully`);
        setActiveDialog(false);
      }
    } catch {
      toast.error('Failed to update member');
    }
    setDeleteLoader(false);
  };

  /** MATCH BY DEFAULT */
  const handleMatchByDefault = async (e: ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setSwitchLoading(true);

    try {
      const response = await API.defaultMatchRequest({
        grpId,
        mentorId: data.userId,
        value: checked
      });

      if (response.status === 200) {
        let defaultMatch = [...(data.defaultMatch || [])];
        checked ? defaultMatch.push(grpId) : (defaultMatch = defaultMatch.filter((x) => x !== grpId));

        dispatch(groupUsersActions.updateUser({ ...data, defaultMatch }));
      }
    } catch {
      toast.error('Default Match failed');
    }

    setSwitchLoading(false);
  };

  /** LEARNING OBJECTIVES */
  const { orgId } = getUserDetails();
  const fetchObjectives = async () => {
    try {
      const response = await API.adminViewObjectives({
        orgId,
        userId: data.userId,
        grpId
      });

      setObjectivesData(response.data.data);
    } catch {
      toast.error('Failed to load learning objectives');
      setObjectivesData([]);
    }
  };

  const handleOpenObjectives = () => {
    fetchObjectives();
    setObjectivesData([]);
  };

  const COLS = {
    checkbox: 22,
    name: 240,
    role: 240,
    lastLogin: 180,
    programs: 80,
    onboarding: 150,
    onboardedDate: 150,
    goalProgress: 140,
    experience: 150,
    meetings: 100,
    actions: 50
  };

  return (
    <>
      <TableRow
        sx={{
          height: 65,
          borderBottom: '1px solid #2A2A2A',
          bgcolor: '#151515',
          '&:hover': { bgcolor: '#151515' }
        }}
      >
        {/* 1 — checkbox */}
        <TableCell sx={{ width: COLS.checkbox }} />

        {/* 2 — NAME */}
        <TableCell sx={{ width: COLS.name }}>
          <Box display="flex" alignItems="center" gap={1}>
              <Typography sx={{ color: '#FDFDFD', fontSize: 14, fontWeight: 600 }}>{data.name}</Typography>
          </Box>
        </TableCell>

        {/* 3 — ROLE */}
        <TableCell sx={{ color: '#FDFDFD',width: COLS.role }}>
          <Box display="flex" gap={1}>
            {data.role.map((r) => (
              <RoleTypeTag key={r} category={_.startCase(r)} place="userTable" />
            ))}
          </Box>
        </TableCell>

        {/* 4 — LAST LOGIN */}
        <TableCell sx={{ color: '#FDFDFD',width: COLS.lastLogin }}>{unixToHumanReadable(data.lastLogin)}</TableCell>

        {/* 5 — PROGRAMS */}
        <TableCell sx={{ color: '#FDFDFD',width: COLS.programs, textAlign: 'center' }}>{data.sentMessages}</TableCell>

        {/* 6 — ONBOARDING STATUS */}
        <TableCell sx={{ color: '#FDFDFD',width: COLS.onboarding }}>{_.startCase(data.onboardStatus)}</TableCell>

        {/* 7 — ONBOARDED DATE */}
        <TableCell sx={{ color: '#FDFDFD',width: COLS.onboardedDate }}>
          {data.onboardedDate ? moment.unix(data.onboardedDate).format('MM/DD/YYYY') : '—'}
        </TableCell>

        {/* 8 — GOAL PROGRESS */}
        <TableCell sx={{ color: '#FDFDFD',width: COLS.goalProgress, textAlign: 'center' }}>
          {data.role.includes('mentee') ? <LinearProgressWithLabel value={data.goalProgress ?? 0} /> : 'N/A'}
        </TableCell>

        {/* 9 — EXPERIENCE POINTS */}
        <TableCell sx={{ color: '#FDFDFD',width: COLS.experience, textAlign: 'center' }}>
          {data.xpPoints ?? 0}
        </TableCell>

        {/* 10 — MEETINGS */}
        <TableCell sx={{ color: '#FDFDFD',width: COLS.meetings, textAlign: 'center' }}>{data.meetings}</TableCell>

        {/* 11 — ACTIONS */}
        <TableCell sx={{ color: '#FDFDFD',width: COLS.actions }}>
          <IconButton onClick={handleClickAction}>
            <MoreVertIcon sx={{ color: '#FDFDFD' }} />
          </IconButton>
        </TableCell>
      </TableRow>

      {/* DELETE DIALOG */}
      <ProgramDeletDialog
        openDelete={openDelete}
        setDelete={setDelete}
        deleteLoader={deleteLoader}
        text="Are you sure you want to remove this member?"
        handleDelete={removeUser}
      />

      {/* ACTIVATE / DEACTIVATE DIALOG */}
      <ProgramDeletDialog
        openDelete={openActive}
        setDelete={setActiveDialog}
        deleteLoader={deleteLoader}
        text={`Are you sure you want to ${data.isActive ? 'deactivate' : 'reactivate'} this member?`}
        handleDelete={activateUser}
        btnText={data.isActive ? 'Deactivate' : 'Reactivate'}
      />
    </>
  );
};

export default ProgramUserTableRow;
